import React, { useState, useRef, useMemo } from 'react';
import { SlidersHorizontal, Calendar, Target, Disc3, Bell, Inbox, Clock, Users, RotateCcw, ChevronRight, Zap } from 'lucide-react';
import { TODAY, df, addDays } from '../utils/dates';
import { DecCard } from './shared';
import css from './DashboardPage.module.css';
import { getCurrentQuote } from '../utils/quoteRotation';
import { computeCrossDomainInsights } from '../utils/intelligence';
import { computeGoalStatus, computeGoalProgress } from '../utils/goals';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function relativeDay(dateStr) {
  const d = df(dateStr);
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  if (d > 0 && d <= 6) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[new Date(dateStr + 'T12:00:00').getDay()];
  }
  if (d > 0) return `in ${d}d`;
  return dateStr.slice(5);
}

function fmtPct(n) { return `${Math.round(n || 0)}%`; }

// ─────────────────────────────────────────────────────────────────────────────
// ProactiveCard — Mylo surfaces a cross-domain insight unprompted
// ─────────────────────────────────────────────────────────────────────────────

const DOMAIN_COLOR = {
  health:   'var(--accent-s)',   // Fix 2: was --grn
  finance:  'var(--accent-g)',
  tasks:    'var(--accent-g)',
  routines: 'var(--accent-s)',
  vinyl:    'var(--accent-s)',
};

const DOMAIN_ACTION_LABEL = {
  health:   'log workout →',
  finance:  'view finance →',
  tasks:    'view tasks →',
  routines: 'check habits →',
  vinyl:    'view vinyl →',
};

function ProactiveCard({ insights, onNav }) {
  if (!insights || insights.length === 0) return null;
  return (
    <div style={{
      background: 'var(--s2)',
      border: '1px solid var(--b2)',
      borderRadius: 'var(--r3)',
      marginBottom: 'var(--sp4)',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: 'var(--sp2) var(--sp3)',   /* Fix 9: tighter header padding */
        borderBottom: '1px solid var(--b1)',
        display: 'flex', alignItems: 'center', gap: 'var(--sp2)',
      }}>
        <Zap size={12} strokeWidth={1.5} style={{ color: 'var(--accent-s)', flexShrink: 0 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-xs)', color: 'var(--t3)', letterSpacing: '.04em' }}>mylo noticed</span>
      </div>
      {insights.map((insight, i) => {
        const borderColor = insight.urgent ? 'var(--accent-d)' : 'var(--accent-s)';
        // Context-aware action routing
        const tab = insight.type === 'goal_at_risk' ? 'finance'
                  : insight.domain === 'health' ? 'health'
                  : insight.domain === 'finance' ? 'finance'
                  : insight.domain === 'tasks' ? 'tasks'
                  : insight.domain === 'routines' ? 'tasks'
                  : insight.domain === 'vinyl' ? 'vinyl'
                  : 'ask';
        const actionLabel = insight.type === 'goal_at_risk' ? 'update goal →'
                          : insight.type === 'bill_due' ? 'view finance →'
                          : DOMAIN_ACTION_LABEL[insight.domain] || 'explore →';
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 'var(--sp3)',
            padding: 'var(--sp2) var(--sp3)',   /* Fix 9: --sp-row vertical, --sp-section horizontal */
            borderTop: i > 0 ? '1px solid var(--b1)' : 'none',
          }}>
            
            <div style={{ width: 2, borderRadius: 1, alignSelf: 'stretch', background: borderColor, flexShrink: 0, minHeight: 20 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--sans)', fontSize: 'var(--fs-sm)', color: 'var(--ink)', lineHeight: 1.5 }}>
                {insight.message}
              </div>
              <div style={{ display: 'flex', gap: 'var(--sp3)', marginTop: 'var(--sp2)' }}>
                <button onClick={() => onNav(tab)} style={{
                  background: 'transparent', border: 'none', padding: 0,
                  fontFamily: 'var(--sans)', fontSize: 'var(--fs-xs)',
                  color: borderColor, cursor: 'pointer', transition: 'opacity .1s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '.7'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >{actionLabel}</button>
                <button onClick={() => onNav('ask')} style={{
                  background: 'transparent', border: 'none', padding: 0,
                  fontFamily: 'var(--sans)', fontSize: 'var(--fs-xs)',
                  color: 'var(--t3)', cursor: 'pointer', transition: 'opacity .1s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '.7'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >ask mylo →</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MomentumStrip — progress bar style (Option A)
// ─────────────────────────────────────────────────────────────────────────────
function MomentumStrip({ st, desktopMode }) {
  var days = useMemo(function() { return Array.from({ length: 7 }, function(_, i) { return addDays(TODAY, -(6 - i)); }); }, []);
  var tasks = st.tasks || [];
  var routines = st.routines || [];
  var workouts = st.workouts || [];
  var taskDays = useMemo(function() { return days.filter(function(d) { return tasks.some(function(t) { return t.done && (t.dueDate === d || t.completedAt === d); }); }).length; }, [days, tasks]);
  var habitDays = useMemo(function() { return days.filter(function(d) { return routines.some(function(r) { return (r.history || []).indexOf(d) !== -1; }); }).length; }, [days, routines]);
  var workoutDays = useMemo(function() { return days.filter(function(d) { return workouts.some(function(w) { return w.date === d; }); }).length; }, [days, workouts]);
  var streak = useMemo(function() { var s = 0; for (var i = 0; i < 30; i++) { var d = addDays(TODAY, -i); if (workouts.some(function(w) { return w.date === d; })) s++; else break; } return s; }, [workouts]);
  var rows = [
    { label: 'tasks',    val: taskDays,    total: 7 },
    { label: 'workouts', val: workoutDays, total: 7 },
    { label: 'habits',   val: habitDays,   total: 7 },
    { label: 'streak',   val: streak,      total: Math.max(streak, 7), isStreak: true },
  ];
  return (
    <div style={desktopMode ? { marginBottom:0 } : { background: 'var(--s2)', border: '0.5px solid var(--b1)', borderRadius: 'var(--r3)', padding: '14px 16px', marginBottom: 'var(--sp6)' }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-xs)', color: 'var(--t4)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12, display: desktopMode ? 'none' : 'block' }}>7-day momentum</div>
      {rows.map(function(row) { return (
        <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: row.label === 'streak' ? 0 : 9 }}>
          <div style={{ width: 58, flexShrink: 0, fontFamily: 'var(--sans)', fontSize: 11, color: 'var(--t3)' }}>{row.label}</div>
          <div style={{ flex: 1, height: 4, background: 'var(--b1)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 2, background: 'var(--accent)', opacity: row.val === 0 ? 0 : row.val / row.total < 0.4 ? 0.45 : 1, width: (row.total > 0 ? Math.min(100, Math.round(row.val / row.total * 100)) : 0) + '%', transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--t4)', width: 32, textAlign: 'right', flexShrink: 0 }}>
            {row.isStreak ? row.val + 'd' : row.val + ' / 7'}
          </div>
        </div>
      ); })}
    </div>
  );
}



function PulseCell({ n, label, sub, colorClass, onClick, primary }) {
  return (
    <div
      className={[css.pulseCell, primary ? css.pulseCellPrimary : ''].join(' ')}
      onClick={onClick}
      role="button"
      aria-label={`${label}: ${n}`}
    >
      <div className={[css.pulseN, !primary && css.pulseNSecondary, colorClass].filter(Boolean).join(' ')}>
        {n}
      </div>
      <div className={css.pulseL}>{label}</div>
      {sub && <div className={css.pulseSub}>{sub}</div>}
    </div>
  );
}

function TaskRow({ task, onToggle, onThisWeek, onLater }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className={[css.taskRow, task.done ? css.taskRowDone : '', expanded ? css.taskRowExpanded : ''].filter(Boolean).join(' ')}>
      <div className={css.taskMain} onClick={() => { if (window.innerWidth <= 680) { setExpanded(e => !e); } else { onToggle(task.id); } }}>
        <div className={[css.taskCheck, task.done ? css.taskCheckDone : ''].join(' ')}>
          {task.done && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <span className={[css.taskText, task.done ? css.taskTextDone : ''].join(' ')}>
          {task.text}
        </span>
        {task.priority === 'high' && !task.done && (
          <span className={css.taskPriorityDot} aria-label="High priority" />
        )}
      </div>
      {!task.done && (
        <div className={css.taskActions}>
          <button className={css.taskChip} onClick={e => { e.stopPropagation(); onToggle(task.id); }}>done</button>
          <button className={css.taskChip} onClick={e => { e.stopPropagation(); onThisWeek(task.id); }}>this week</button>
          <button className={css.taskChip} onClick={e => { e.stopPropagation(); onLater(task.id); }}>later</button>
        </div>
      )}
    </div>
  );
}

function QuickAddInput({ value, onChange, onKeyDown, onAdd, className }) {
  return (
    <div className={className}>
      <input
        className={css.quickAddInput}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder="add a task…"
        aria-label="Add task"
      />
      <button className={css.quickAddBtn} onClick={onAdd} disabled={!value.trim()} aria-label="Add task">
        +
      </button>
    </div>
  );
}

// Section C preview card — quieter label (previewLabel, not sectionLabel)
function PreviewCard({ label, children, onTap, variant, rich }) {
  const classes = [
    css.previewCard,
    variant === 'alt' ? css.previewCardAlt : '',
    rich ? css.previewCardRich : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={classes} onClick={onTap} role="button">
      <div className={css.previewHdr}>
        <span className={rich ? css.previewLabelRich : css.previewLabel}>{label}</span>
        <span className={css.previewArrow}>→</span>
      </div>
      {children}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Pulse metric registry
// ─────────────────────────────────────────────────────────────────────────────

function buildMetric(key, st, derived) {
  const { openTasks, doneTasks, highPriorityCount, paydayDays, paydayN, paydayWarn,
          workoutsThisWeek, todayEvts, trainingStreak } = derived;

  switch (key) {
    case 'tasks':
      return {
        n: openTasks.length, label: 'track today',
        sub: highPriorityCount > 0 ? `${highPriorityCount} high priority`
           : doneTasks.length > 0   ? `· ${doneTasks.length} done today` : 'none open',
        tab: 'tasks',
      };
    case 'tasksHi':
      return {
        n: highPriorityCount, label: 'high priority',
        sub: highPriorityCount === 0 ? 'clear' : 'tasks',
        tab: 'tasks',
      };
    case 'payday':
      return {
        n: paydayN, label: 'payday',
        sub: paydayDays !== null ? (paydayDays <= 7 ? 'this week' : 'upcoming') : 'not set',
        tab: 'finance',
      };
    case 'workouts':
      return {
        n: workoutsThisWeek, label: 'workouts',
        sub: 'this week',
        tab: 'health',
      };
    case 'goals': {
      const active = (st.goals || []).filter(g => !g.archived).length;
      return { n: active, label: 'goals', sub: active === 0 ? 'none set' : 'active', tab: 'goals' };
    }
    case 'vinyl': {
      const inTransit = (st.vinyl || []).filter(v => !v.delivered && v.status === 'shipped').length;
      return { n: inTransit, label: 'vinyl', sub: inTransit === 0 ? 'none inbound' : 'in transit', tab: 'vinyl' };
    }
    case 'events':
      return { n: todayEvts.length, label: 'schedule',
               sub: todayEvts.length === 0 ? 'open day' : 'today',
               tab: 'calendar' };
    case 'reminders': {
      const due = (st.reminders || []).filter(r => !r.done && r.date === TODAY).length;
      return { n: due, label: 'reminders', sub: due === 0 ? 'none today' : 'due today', tab: 'reminders' };
    }
    case 'decisions': {
      const pending = (st.decisions || []).filter(d => !d.resolved).length;
      return { n: pending, label: 'decisions', sub: pending === 0 ? 'none pending' : 'pending', tab: 'dashboard' };
    }
    case 'streak':
      return { n: trainingStreak, label: 'streak',
               sub: trainingStreak === 0 ? 'days' : trainingStreak === 1 ? 'day' : 'days',
               tab: 'health' };
    default:
      return null;
  }
}

const METRIC_CATALOG = [
  { key: 'tasks',     label: 'Tasks today' },
  { key: 'tasksHi',   label: 'High priority tasks' },
  { key: 'payday',    label: 'Payday countdown' },
  { key: 'workouts',  label: 'Workouts this week' },
  { key: 'goals',     label: 'Active goals' },
  { key: 'vinyl',     label: 'Vinyl in transit' },
  { key: 'events',    label: 'Events today' },
  { key: 'reminders', label: 'Reminders due today' },
  { key: 'decisions', label: 'Pending decisions' },
  { key: 'streak',    label: 'Training streak' },
];

const DEFAULT_PULSE = ['tasks', 'payday', 'workouts', 'goals'];
const MAX_PULSE = 4;

// ─────────────────────────────────────────────────────────────────────────────
// PulseCustomizeModal — checkbox + drag-to-reorder
// ─────────────────────────────────────────────────────────────────────────────

function PulseCustomizeModal({ selected, onClose, onSave }) {
  const [picked, setPicked] = useState(selected.slice());
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const toggle = (key) => {
    if (picked.includes(key)) {
      setPicked(picked.filter(k => k !== key));
    } else if (picked.length < MAX_PULSE) {
      setPicked([...picked, key]);
    }
  };

  const orderedKeys = [...picked, ...METRIC_CATALOG.map(m => m.key).filter(k => !picked.includes(k))];

  const onDragStart = (e, idx) => {
    if (idx >= picked.length) { e.preventDefault(); return; }
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e, idx) => {
    if (dragIdx === null || idx >= picked.length) return;
    e.preventDefault();
    setOverIdx(idx);
  };
  const onDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || idx >= picked.length) { setDragIdx(null); setOverIdx(null); return; }
    const next = picked.slice();
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setPicked(next);
    setDragIdx(null);
    setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <div className="pc-backdrop" onClick={onClose}>
      <div className="pc-modal" onClick={e => e.stopPropagation()}>
        <div className="pc-hdr">
          <div>
            <div className="pc-hdr-title">Customize pulse</div>
            <div className="pc-hdr-sub">Pick up to {MAX_PULSE}. Drag selected rows to reorder.</div>
          </div>
          <button className="pc-hdr-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="pc-list">
          {orderedKeys.map((key, idx) => {
            const meta = METRIC_CATALOG.find(m => m.key === key);
            if (!meta) return null;
            const on = picked.includes(key);
            const pos = on ? picked.indexOf(key) + 1 : null;
            const classes = [
              'pc-item',
              on ? 'pc-item-on' : '',
              dragIdx === idx ? 'pc-item-dragging' : '',
              overIdx === idx && dragIdx !== null && dragIdx !== idx ? 'pc-item-dragover' : '',
            ].filter(Boolean).join(' ');
            return (
              <div
                key={key}
                className={classes}
                draggable={on}
                onDragStart={e => onDragStart(e, idx)}
                onDragOver={e => onDragOver(e, idx)}
                onDrop={e => onDrop(e, idx)}
                onDragEnd={onDragEnd}
                onClick={() => toggle(key)}
              >
                <span className="pc-drag" aria-hidden="true">⋮⋮</span>
                <span className="pc-check" aria-hidden="true">
                  <svg className="pc-check-mark" viewBox="0 0 8 8" fill="none">
                    <path d="M1 4l2 2 4-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="pc-label">{meta.label}</span>
                {pos !== null && <span className="pc-meta">{pos}</span>}
              </div>
            );
          })}
        </div>
        <div className="pc-footer">
          <span className={`pc-count${picked.length === MAX_PULSE ? ' pc-count-max' : ''}`}>
            {picked.length} / {MAX_PULSE} selected
          </span>
          <button className="pc-done" onClick={() => onSave(picked)} disabled={picked.length === 0}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

function DashboardPage({ brief=[], sigs=[], patterns=[], st={}, setTab, toggleTask, setSt, notify }) {
  const [taskInput, setTaskInput] = useState('');
  const [showAll,   setShowAll]   = useState(false);
  const [coachDismissed, setCoachDismissed] = useState(() => {
    try { return localStorage.getItem('mylo_coach_card_' + TODAY) === '1'; } catch { return false; }
  });
  const dismissCoach = () => {
    setCoachDismissed(true);
    try { localStorage.setItem('mylo_coach_card_' + TODAY, '1'); } catch {}
  };

  // Ref for smooth-scroll to Section C
  const sectionCRef = useRef(null);

  const scrollToSectionC = () => {
    sectionCRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Greeting ──────────────────────────────────────────────────────────────
  const hour     = new Date().getHours();
  const greeting = hour < 5  ? 'Still up'
                 : hour < 12 ? 'Good morning'
                 : hour < 17 ? 'Good afternoon'
                 : hour < 21 ? 'Good evening'
                 : 'Good night';
  const firstName = st.profile?.name ? st.profile.name.split(' ')[0] : '';

  // ── Today data ────────────────────────────────────────────────────────────
  const todayTasks = useMemo(() => {
    const all = (st.tasks || []).filter(t => t.bucket === 'today' || t.dueDate === TODAY);
    return [...all.filter(t => !t.done), ...all.filter(t => t.done)];
  }, [st.tasks]);
  const openTasks  = useMemo(() => todayTasks.filter(t => !t.done), [todayTasks]);
  const doneTasks  = useMemo(() => todayTasks.filter(t =>  t.done), [todayTasks]);
  const todayEvts  = useMemo(() =>
    (st.events || []).filter(e => e.date === TODAY)
      .sort((a, b) => (a.time || '').localeCompare(b.time || '')), [st.events]);
  const alerts     = useMemo(() => sigs.filter(s => s.lvl === 'warn').slice(0, 3), [sigs]);
  const topInsights = useMemo(() => {
    try {
      const insights = computeCrossDomainInsights(st);

      // Goal at-risk alerts (additive — intelligence.js doesn't produce these)
      const goalAlerts = (st.goals || [])
        .map(g => ({ goal: g, status: computeGoalStatus(g) }))
        .filter(({ status }) => status.status === 'at-risk')
        .map(({ goal, status }) => ({
          id: `goal-${goal.id}`,
          type: 'goal_at_risk',
          priority: 1,
          urgent: true,
          domain: 'finance',
          message: `${goal.title} — ${status.daysRemaining}d left, ${Math.round(status.progressPct)}% complete`,
          action: 'view_finance',
        }));

      // Dedup: intelligence.js uses bill.id in seenFinanceIds but doesn't set insight.id.
      // Key on finance id prefix if present, otherwise message text.
      const seen = new Set();
      const deduped = [];
      for (const ins of [...goalAlerts, ...insights]) {
        const key = ins.id || ins.message;
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(ins);
        }
      }

      return deduped.slice(0, 2);
    } catch { return []; }
  }, [st]);

  // ── Finance signal ────────────────────────────────────────────────────────
  const financeSignal = useMemo(() => {
    const nextPayday = (st.income || []).filter(i => i.nextDate)
      .sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate))[0];
    const paydayDays = nextPayday ? df(nextPayday.nextDate) : null;
    const nextBill = (st.finance || []).filter(f => f.next)
      .sort((a, b) => df(a.next) - df(b.next))
      .find(f => df(f.next) >= 0 && df(f.next) <= 7);
    const billDays = nextBill ? df(nextBill.next) : null;

    if (paydayDays !== null && (billDays === null || paydayDays <= billDays)) {
      const isToday = paydayDays === 0;
      return {
        label: 'payday',
        value: isToday ? 'today' : paydayDays === 1 ? 'tomorrow' : `in ${paydayDays}d`,
        // payday-today = gold (finance-positive); payday-tomorrow = base accent; else neutral
        valueClass: isToday ? css.signalValueGold : paydayDays === 1 ? css.signalValueWarn : undefined,
      };
    }
    if (nextBill) {
      const overdue = billDays < 0;
      return {
        label: nextBill.name,
        value: overdue ? 'overdue' : billDays === 0 ? 'due today' : billDays === 1 ? 'due tomorrow' : `due in ${billDays}d`,
        valueClass: overdue ? css.signalValueDanger : billDays <= 1 ? css.signalValueWarn : undefined,
      };
    }
    return null;
  }, [st.income, st.finance]);

  // ── Health signal ─────────────────────────────────────────────────────────
  const healthSignal = useMemo(() => {
    if (brief?.training) return { label: 'training today', value: brief.training };
    const raceGoal = (st.goals || []).find(g => /marathon|race|5k|10k|half/i.test(g.title || ''));
    if (raceGoal?.targetDate) {
      const d = df(raceGoal.targetDate);
      if (d >= 0 && d <= 60) return { label: raceGoal.title.toLowerCase(), value: d === 0 ? 'today' : `${d}d away` };
    }
    const wk = (st.workouts || []).filter(w => df(w.date) <= 0 && df(w.date) >= -6).length;
    if (wk > 0) return { label: 'workouts this week', value: String(wk) };
    return null;
  }, [brief?.training, st.goals, st.workouts]);

  // ── Section C data ────────────────────────────────────────────────────────
  const calPreview = useMemo(() =>
    (st.events || [])
      .filter(e => df(e.date) >= 0 && e.date !== TODAY)
      .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''))
      .slice(0, 2),
    [st.events]);

  // topGoal removed — was dead code using stale g.progress field

  const vinylInTransit = useMemo(() =>
    (st.vinyl || []).filter(v => !v.delivered && v.status === 'shipped' && v.eta && df(v.eta) >= 0 && df(v.eta) <= 7)
      .sort((a, b) => df(a.eta) - df(b.eta)), [st.vinyl]);
  const vinylRecent = useMemo(() =>
    (st.vinyl || []).filter(v => v.delivered || v.status !== 'shipped')
      .sort((a, b) => new Date(b.ordered || b.dateAdded || 0) - new Date(a.ordered || a.dateAdded || 0))[0],
    [st.vinyl]);

  // ── Pulse ─────────────────────────────────────────────────────────────────
  const paydayDays = useMemo(() => {
    const p = (st.income || []).filter(i => i.nextDate)
      .sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate))[0];
    return p ? df(p.nextDate) : null;
  }, [st.income]);

  const workoutsThisWeek = useMemo(() =>
    (st.workouts || []).filter(w => df(w.date) <= 0 && df(w.date) >= -6).length, [st.workouts]);

  const highPriorityCount = useMemo(() => openTasks.filter(t => t.priority === 'high').length, [openTasks]);
  const paydayN    = paydayDays === 0 ? 'today' : paydayDays === 1 ? '1d' : paydayDays !== null ? `${paydayDays}d` : '—';
  const paydayWarn = paydayDays !== null && paydayDays <= 1;

  const trainingStreak = useMemo(() => {
    const days = new Set((st.workouts || []).map(w => w.date));
    let streak = 0;
    const cur = new Date();
    for (let i = 0; i < 365; i++) {
      const iso = cur.toISOString().slice(0, 10);
      if (days.has(iso)) { streak++; cur.setDate(cur.getDate() - 1); }
      else break;
    }
    return streak;
  }, [st.workouts]);

  // ── Proactive coach message (priority waterfall, no AI call) ───────────────
  const coachMessage = useMemo(() => {
    const tasks    = st.tasks    || [];
    const workouts = st.workouts || [];
    const finance  = st.finance  || [];
    const income   = st.income   || [];
    const goals    = st.goals    || [];
    const now      = new Date();
    const hour     = now.getHours();

    // 1. Race proximity + workout gap
    const raceDate = st.raceDate || (goals.find(g => g.targetDate && /marathon|race|5k|10k|half|triathlon/i.test(g.title||''))?.targetDate);
    if (raceDate) {
      const raceDays = Math.round((new Date(raceDate + 'T12:00:00') - now) / 86400000);
      if (raceDays >= 0 && raceDays <= 14) {
        const lastWkt = workouts.length > 0 ? Math.abs(df(workouts[0].date)) : 99;
        if (lastWkt >= 3)
          return { msg: `race in ${raceDays}d — last run was ${lastWkt}d ago`, tab: 'health', urgency: 'warn' };
        return { msg: `race in ${raceDays}d — you're in the window`, tab: 'health', urgency: 'info' };
      }
    }

    // 2. Bill due soon (≥$200 in ≤2 days)
    const urgentBill = finance.filter(f => f.next).find(f => {
      const d = df(f.next);
      return d >= 0 && d <= 2 && parseFloat(f.amount||0) >= 200;
    });
    if (urgentBill) {
      const d = df(urgentBill.next);
      return { msg: `${urgentBill.name} $${urgentBill.amount} due ${d===0?'today':d===1?'tomorrow':'in '+d+'d'}`, tab: 'finance', urgency: 'warn' };
    }

    // 3. Payday today or tomorrow
    const nextPay = income.filter(i => i.nextDate).sort((a,b) => new Date(a.nextDate)-new Date(b.nextDate))[0];
    if (nextPay?.nextDate) {
      const pd = df(nextPay.nextDate);
      if (pd === 0) return { msg: `payday today — ${finance.filter(f=>f.next&&df(f.next)>=0&&df(f.next)<=7).length} bills this week`, tab: 'finance', urgency: 'info' };
      if (pd === 1) return { msg: `payday tomorrow`, tab: 'finance', urgency: 'info' };
    }

    // 4. Workout gap vs baseline (midweek, no workouts)
    if (workouts.length >= 4 && hour >= 14) {
      const thisWeekWkts = workouts.filter(w => df(w.date) <= 0 && df(w.date) >= -6).length;
      const last4Avg = workouts.filter(w => df(w.date) >= -34 && df(w.date) < -6).length / 4;
      if (last4Avg >= 2 && thisWeekWkts === 0 && now.getDay() >= 3)
        return { msg: `no workouts yet this week — your average is ${last4Avg.toFixed(1)}/week`, tab: 'health', urgency: 'warn' };
    }

    // 5. Overdue tasks
    const overdue = tasks.filter(t => !t.done && t.dueDate && df(t.dueDate) < 0).length;
    if (overdue >= 3) return { msg: `${overdue} overdue tasks`, tab: 'tasks', urgency: 'warn' };

    // 6. Positive: good week
    const thisWeekDone = tasks.filter(t => t.done && t.completedAt && df(t.completedAt) >= -6).length;
    const thisWeekWkts2 = workouts.filter(w => df(w.date) <= 0 && df(w.date) >= -6).length;
    if (thisWeekDone >= 5 && thisWeekWkts2 >= 3)
      return { msg: `${thisWeekDone} tasks done + ${thisWeekWkts2} workouts this week`, tab: null, urgency: 'positive' };

    return null;
  }, [st, TODAY]);

  const selectedPulse = (st.prefs && Array.isArray(st.prefs.pulseMetrics) && st.prefs.pulseMetrics.length > 0)
    ? st.prefs.pulseMetrics.slice(0, MAX_PULSE)
    : DEFAULT_PULSE;

  const pulseDerived = {
    openTasks, doneTasks, highPriorityCount,
    paydayDays, paydayN, paydayWarn,
    workoutsThisWeek, todayEvts, trainingStreak,
  };

  const pulseCells = selectedPulse
    .map(key => buildMetric(key, st, pulseDerived))
    .filter(Boolean)
    .map((cell, i) => {
      const isActionable = cell.n !== 0 && cell.n !== '—' ? true : false;
      const primary = i === 0 && isActionable;
      return { ...cell, primary };
    });

  const [pulseCustomizeOpen, setPulseCustomizeOpen] = useState(false);
  const savePulse = (keys) => {
    setSt(p => ({ ...p, prefs: { ...(p.prefs || {}), pulseMetrics: keys } }));
    setPulseCustomizeOpen(false);
  };

  // ── Quick-add ─────────────────────────────────────────────────────────────
  const addQuickTask = () => {
    if (!taskInput.trim()) return;
    setSt(p => ({
      ...p,
      tasks: [{ id: `t${Date.now()}`, text: taskInput.trim(), bucket: 'today', dueDate: TODAY,
                done: false, created: TODAY, priority: 'medium', listId: 'l1', recurring: 'none' },
               ...p.tasks],
    }));
    setTaskInput('');
    notify('Task added');
  };

  const moveTask = (id, bucket) => {
    setSt(p => ({ ...p, tasks: (p.tasks||[]).map(x => x.id === id ? { ...x, bucket } : x) }));
    notify(`Moved to ${bucket === 'thisWeek' ? 'this week' : bucket}`);
  };

  const quickAddProps = {
    value: taskInput,
    onChange: e => setTaskInput(e.target.value),
    onKeyDown: e => e.key === 'Enter' && addQuickTask(),
    onAdd: addQuickTask,
  };

  const visibleTasks = showAll ? todayTasks : todayTasks.slice(0, 5);


  // ── Quote ─────────────────────────────────────────────────────────────────
  const QUOTES = [
    { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
    { text: "Small steps every day.", author: null },
    { text: "What you do today can improve all your tomorrows.", author: "Ralph Marston" },
    { text: "It always seems impossible until it's done.", author: "Nelson Mandela" },
    { text: "Focus on progress, not perfection.", author: null },
  ];
  const q = QUOTES[new Date().getDate() % QUOTES.length];

  // ── Calendar events today ──────────────────────────────────────────────────
  const calEvents = st.calEvents || st.events || [];
  const todayEvents = calEvents.filter(e => e.date === TODAY);

  // ── Task actions ───────────────────────────────────────────────────────────
  const snoozeTask = (id) => {
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate()+1);
    const d = tomorrow.toISOString().slice(0,10);
    setSt(p => ({ ...p, tasks: p.tasks.map(t => t.id===id ? {...t, dueDate:d, bucket:'later'} : t) }));
    notify('Snoozed to tomorrow');
  };
  const deferTask = (id) => {
    setSt(p => ({ ...p, tasks: p.tasks.map(t => t.id===id ? {...t, bucket:'later'} : t) }));
    notify('Moved to later');
  };

  const today = new Date();
  const monthNames = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
  const todayLabel = monthNames[today.getMonth()] + ' ' + today.getDate();

  const panelRef = React.useRef(null);
  React.useEffect(() => {
    if (panelRef.current) panelRef.current.scrollLeft = 0;
  }, []);
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
  React.useEffect(function() {
    function handleResize() { setIsMobile(window.innerWidth <= 900); }
    window.addEventListener('resize', handleResize);
    return function() { window.removeEventListener('resize', handleResize); };
  }, []);

  // ── AI Daily Brief ─────────────────────────────────────────────────────────
  const [aiBrief, setAiBrief] = React.useState(function() {
    try { 
      var cached = localStorage.getItem('mylo_ai_brief_' + TODAY);
      return cached || '';
    } catch { return ''; }
  });
  const [briefLoading, setBriefLoading] = React.useState(false);

  React.useEffect(function() {
    var pendingCount = (st.pendingEmailActions||[]).filter(function(a){return a.status==='pending';}).length;
    var cacheKey = 'mylo_ai_brief_' + TODAY + '_p' + pendingCount;
    // Clear old cache keys for today
    try {
      Object.keys(localStorage).filter(function(k){return k.startsWith('mylo_ai_brief_' + TODAY);}).forEach(function(k){
        if (k !== cacheKey) localStorage.removeItem(k);
      });
      if (localStorage.getItem(cacheKey)) return;
    } catch {}
    if (briefLoading) return;
    setBriefLoading(true);

    // Build context from state
    var tasks = (st.tasks||[]).filter(function(t){return !t.done && t.bucket==='today';});
    var workouts = (st.workouts||[]).filter(function(w){ var d=Math.round((new Date(TODAY)-new Date(w.date))/86400000); return d>=0&&d<7; });
    var goals = (st.goals||[]).filter(function(g){return !g.completed;}).slice(0,3);
    var bills = (st.finance||[]).filter(function(f){ var d=Math.round((new Date(f.next||'')-new Date(TODAY))/86400000); return d>=0&&d<=7; });
    var lastWorkout = (st.workouts||[]).sort(function(a,b){return new Date(b.date)-new Date(a.date);})[0];
    var daysSinceWorkout = lastWorkout ? Math.round((new Date(TODAY)-new Date(lastWorkout.date))/86400000) : null;
    var ctx = [
      'User: Kyle, EM physician, overnight shifts 11pm-7am, Chicago.',
      'Today: ' + TODAY + '.',
      daysSinceWorkout !== null ? 'Last workout: ' + daysSinceWorkout + ' days ago.' : 'No workouts logged.',
      'Tasks today: ' + (tasks.length > 0 ? tasks.slice(0,3).map(function(t){return t.text;}).join(', ') : 'none') + '.',
      'Bills this week: ' + (bills.length > 0 ? bills.map(function(f){return f.name;}).join(', ') : 'none') + '.',
      'Goals: ' + (goals.length > 0 ? goals.map(function(g){return g.title;}).join(', ') : 'none') + '.',
      'Workouts this week: ' + workouts.length + '/7 (PPL split target).',
      (function() {
        var pending = (st.pendingEmailActions||[]).filter(function(a){return a.status==='pending';});
        if (!pending.length) return '';
        var vinyl = pending.filter(function(a){return a.type==='vinyl_order'||a.type==='vinyl_shipped'||a.type==='vinyl_delivered';});
        if (vinyl.length) return 'Vinyl detected from Gmail: ' + vinyl.map(function(a){return (a.vendor||'unknown') + ' (' + a.type.replace('vinyl_','') + ')';}).join(', ') + '.';
        return '';
      })()
    ].join(' ');
    var sysPrompt = 'You are Mylo, Kyle personal AI chief of staff. Kyle is an emergency medicine physician in Chicago, works overnight shifts 11pm-7am, is on an H-1B visa pursuing a green card via NIW/HPSA, runs marathons, lifts 6x/week (PPL split), follows a mostly plant-based diet with chicken, and is working toward FIRE at 42. Write a 2-3 sentence daily brief that is specific and actionable — use the actual data provided, mention real numbers and deadlines, lead with the most urgent item. Sound like a smart assistant who knows his life. No greetings, no preamble, just the brief.';
    fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system: sysPrompt, messages: [{ role: 'user', content: ctx }], max_tokens: 120, stream: false, userApiKey: st.anthropicKey || localStorage.getItem('mylo_api_key') || '' })
    }).then(function(r){ return r.json(); }).then(function(data) {
      var text = data.content && data.content[0] && data.content[0].text ? data.content[0].text.trim() : '';
      if (text) { try { localStorage.setItem('mylo_ai_brief_' + TODAY, text); } catch {} setAiBrief(text); }
      setBriefLoading(false);
    }).catch(function() { setBriefLoading(false); });
  }, []);

  return (
    <div className={css.pageWrapper}>

      {pulseCustomizeOpen && (
        <PulseCustomizeModal
          selected={selectedPulse}
          onClose={() => setPulseCustomizeOpen(false)}
          onSave={metrics => { setSt(p => ({ ...p, prefs: { ...(p.prefs||{}), pulseMetrics: metrics } })); setPulseCustomizeOpen(false); }}
        />
      )}

      {/* ── Two-panel swipe container ── */}
      <div ref={panelRef} style={{
        display: 'flex',
        flexDirection: 'row',
        overflowX: isMobile ? 'scroll' : 'visible',
        overflowY: isMobile ? 'hidden' : 'visible',
        scrollSnapType: isMobile ? 'x mandatory' : 'none',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
        width: '100%',
        height: isMobile ? 'calc(100dvh - 56px - env(safe-area-inset-bottom) - 52px)' : 'auto',
      }}>

        {/* ══ PANEL 1 — Brief ══ */}
        <div style={{
          minWidth: isMobile ? '100%' : 0,
          width: isMobile ? '100%' : 0,
          scrollSnapAlign: 'start',
          flexShrink: 0,
          display: isMobile ? 'flex' : 'none',
          flexDirection: 'column',
          height: isMobile ? 'calc(100dvh - 56px - env(safe-area-inset-bottom) - 52px)' : 0,
          overflow: 'hidden',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}>

          {/* ── Greeting ── */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'16px 20px 0' }}>
            <div>
              <div style={{ fontFamily:'var(--sans)', fontSize:26, fontWeight:300, color:'var(--ink)', letterSpacing:'-0.025em', lineHeight:1.2 }}>{greeting + ','}</div>
              <div style={{ fontFamily:'var(--sans)', fontSize:26, fontWeight:500, color:'var(--accent)', letterSpacing:'-0.025em', lineHeight:1.15 }}>{firstName || 'there'}.</div>
            </div>
            <div style={{ textAlign:'right', paddingTop:4 }}>
              <div style={{ fontFamily:'var(--mono)', fontSize:10, color:'var(--al)', letterSpacing:'0.05em' }}>{todayLabel ? todayLabel.toUpperCase() : ''}</div>
              <div style={{ fontFamily:'var(--mono)', fontSize:9, color:'var(--t4)', marginTop:1 }}>{'week ' + Math.ceil((Math.floor((new Date()-new Date(new Date().getFullYear(),0,1))/86400000)+1)/7)}</div>
            </div>
          </div>

          {q && <div style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t4)', fontStyle:'italic', textAlign:'center', padding:'0 20px' }}>"{q.text}"</div>}

          <div style={{ height:'0.5px', background:'var(--b1)', margin:'12px 20px' }} />

          {/* ── Brief ── */}
          <div style={{ padding:'0 20px' }}>
            <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:6 }}>mylo · today</div>
            <div style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--t2)', lineHeight:1.6 }}>
              {aiBrief || (coachMessage && !coachDismissed ? coachMessage.msg : (topInsights.length > 0 ? (topInsights[0].message || topInsights[0].msg) : 'Add tasks and workouts — Mylo will brief you here.'))}
            </div>
          </div>

          <div style={{ height:'0.5px', background:'var(--b1)', margin:'12px 20px' }} />

          {/* ── Hero stats ── */}
          <div style={{ display:'flex', alignItems:'flex-end', padding:'0 20px' }}>
            <div style={{ flex:1, textAlign:'center' }}>
              <div style={{ fontFamily:'var(--mono)', fontSize:40, fontWeight:300, color:'var(--accent)', lineHeight:1 }}>{todayTasks.length}</div>
              <div style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t4)', marginTop:5 }}>tasks today</div>
            </div>
            <div style={{ width:'0.5px', background:'var(--b1)', height:40, alignSelf:'center' }} />
            <div style={{ flex:1, textAlign:'center' }}>
              <div style={{ fontFamily:'var(--mono)', fontSize:40, fontWeight:300, color:'var(--ink)', lineHeight:1 }}>{(function(){ var wks=(st.workouts||[]).filter(function(w){ var d=Math.round((new Date(TODAY)-new Date(w.date))/86400000); return d>=0&&d<7; }); var mi=wks.reduce(function(s,w){return s+(w.miles||w.dist||0);},0); return mi>0?mi:'0'; })()}</div>
              <div style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t4)', marginTop:5 }}>miles · wk</div>
            </div>
            <div style={{ width:'0.5px', background:'var(--b1)', height:40, alignSelf:'center' }} />
            <div style={{ flex:1, textAlign:'center' }}>
              <div style={{ fontFamily:'var(--mono)', fontSize:40, fontWeight:300, color:'var(--ink)', lineHeight:1 }}>{(function(){ var s=0; var wks=st.workouts||[]; for(var i=0;i<30;i++){var dd=new Date(TODAY);dd.setDate(dd.getDate()-i);var ds=dd.toISOString().slice(0,10);if(wks.some(function(w){return w.date===ds;}))s++;else break;} return s; })()}</div>
              <div style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t4)', marginTop:5 }}>day streak</div>
            </div>
          </div>

          <div style={{ height:'0.5px', background:'var(--b1)', margin:'12px 20px' }} />

          {/* ── Tasks ── */}
          <div style={{ padding:'0 20px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontFamily:'var(--mono)', fontSize:9, color:'var(--t4)', textTransform:'uppercase', letterSpacing:'0.1em' }}>today's tasks</div>
              <div style={{ fontFamily:'var(--mono)', fontSize:9, color:'var(--t4)' }}>{openTasks.length} open</div>
            </div>
            {todayTasks.length === 0 && <div style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t4)', fontStyle:'italic' }}>no tasks for today</div>}
            {openTasks.slice(0,3).map(function(t) { return (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderBottom:'0.5px solid var(--b1)' }}>
                <div style={{ width:16, height:16, borderRadius:4, border:'1px solid var(--b2)', flexShrink:0 }} onClick={function(){ if(toggleTask) toggleTask(t.id); }} />
                <div style={{ flex:1, fontFamily:'var(--sans)', fontSize:13, color:'var(--t2)' }}>{t.text || t.title}</div>
                {t.listId && <div style={{ fontFamily:'var(--mono)', fontSize:8, color:'var(--accent)', background:'var(--aw)', border:'0.5px solid var(--ab)', borderRadius:3, padding:'2px 5px' }}>{t.listId}</div>}
              </div>
            ); })}
          </div>

          <div style={{ height:'0.5px', background:'var(--b1)', margin:'12px 20px' }} />

          {/* ── Schedule ── */}
          <div style={{ padding:'0 20px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <div style={{ fontFamily:'var(--mono)', fontSize:9, color:'var(--t4)', textTransform:'uppercase', letterSpacing:'0.1em' }}>schedule · today</div>
              <div style={{ fontFamily:'var(--mono)', fontSize:9, color:'var(--t4)' }}>{todayEvents.length} event{todayEvents.length!==1?'s':''}</div>
            </div>
            {todayEvents.length === 0 && <div style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t4)', fontStyle:'italic' }}>nothing scheduled today</div>}
            {todayEvents.slice(0,3).map(function(e,i) { return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom: i<todayEvents.length-1 ? '0.5px solid var(--b1)' : 'none' }}>
                <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--accent)', width:40, flexShrink:0 }}>{e.time||'—'}</div>
                <div style={{ width:'0.5px', height:14, background:'var(--b1)', flexShrink:0 }} />
                <div style={{ flex:1, fontFamily:'var(--sans)', fontSize:12, color:'var(--ink)' }}>{e.title}</div>
              </div>
            ); })}
          </div>

          {/* ── Swipe indicator ── */}
          <div style={{ marginTop:'auto', padding:'12px 0 8px' }}>
            <div style={{ display:'flex', gap:5, justifyContent:'center', marginBottom:4 }}>
              <div style={{ width:18, height:3, borderRadius:2, background:'var(--accent)', opacity:0.8 }} />
              <div style={{ width:5, height:5, borderRadius:'50%', background:'var(--b2)' }} />
            </div>
            <div style={{ fontFamily:'var(--mono)', fontSize:8, color:'var(--t4)', letterSpacing:'0.08em', textAlign:'center' }}>swipe for full dashboard →</div>
          </div>
        </div>

        {/* ══ PANEL 2 — Full Dashboard ══ */}
        <div style={{
          minWidth: '100%',
          width: '100%',
          scrollSnapAlign: 'start',
          flexShrink: 0,
          height: isMobile ? 'calc(100dvh - 56px - env(safe-area-inset-bottom))' : 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: isMobile ? 0 : '0',
        }}>

          <div style={{ padding: isMobile ? '16px 16px 140px' : '32px 40px 40px' }}>
          {/* ── HEADER ── */}
          <div style={{ paddingBottom:16, borderBottom:'0.5px solid var(--b1)', marginBottom:20 }}>
            <div style={{ fontFamily:'var(--sans)', fontSize:17, fontWeight:300, color:'var(--ink)', letterSpacing:'-0.02em', marginBottom:6 }}>Kyle's dashboard</div>
            {q && <div style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t4)', fontStyle:'italic', textAlign:'center' }}>"{q.text}"</div>}
          </div>

          {/* ── COACH BRIEF ── */}
          {(alerts.length > 0 || topInsights.length > 0) && (
            <div style={{ paddingBottom:28, borderBottom:'0.5px solid var(--b1)', marginBottom:28 }}>
              <div style={{ fontFamily:'var(--mono)', fontSize:9, color:'var(--t4)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:10 }}>mylo · daily brief</div>
              <div style={{ fontFamily:'var(--sans)', fontSize:14, color:'var(--t2)', lineHeight:1.75 }}>
                {topInsights.length > 0 ? (topInsights[0].message || topInsights[0].msg || 'You have new insights.') : (alerts[0] && alerts[0].msg) || ''}
              </div>
            </div>
          )}

          {/* ── PULSE NUMBERS ── */}
          {(
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:0, paddingBottom:28, borderBottom:'0.5px solid var(--b1)', marginBottom:28 }}>
              {pulseCells.slice(0,5).map(function(cell,i) { return (
                <div key={i} style={{ padding:'0 20px 0 0', paddingLeft: i > 0 ? 20 : 0, borderRight: i < 4 ? '0.5px solid var(--b1)' : 'none', cursor:'pointer' }} onClick={function() { setTab(cell.tab); }}>
                  <div style={{ fontFamily:'var(--mono)', fontSize:'34px', fontWeight:300, color: i===0 ? 'var(--accent)' : 'var(--ink)', lineHeight:1, marginBottom:5, letterSpacing:'-0.02em' }}>{cell.n}</div>
                  <div style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t4)', marginTop:6 }}>{cell.label}</div>
                </div>
              ); })}
            </div>
          )}

          {/* ── MOMENTUM + AT A GLANCE ── */}
          <div style={{ paddingBottom:28, borderBottom:'0.5px solid var(--b1)', marginBottom:28 }}>
            {isMobile ? (
              <div>
                <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:12 }}>Momentum</div>
                <MomentumStrip st={st} desktopMode={true} />
                <div style={{ marginTop:16 }}>
                  <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:12 }}>At a Glance</div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'0.5px solid var(--b1)', cursor:'pointer' }} onClick={function(){ setTab('finance'); }}>
                    <span style={{ fontSize:13, color:'var(--t3)' }}>Finance</span>
                    <span style={{ fontSize:13, color:'var(--ink)' }}>{financeSignal ? financeSignal.label : 'Nothing due'}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'0.5px solid var(--b1)', cursor:'pointer' }} onClick={function(){ setTab('health'); }}>
                    <span style={{ fontSize:13, color:'var(--t3)' }}>Health</span>
                    <span style={{ fontSize:13, color:'var(--ink)' }}>{healthSignal ? healthSignal.label : 'No workouts'}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', cursor:'pointer' }} onClick={function(){ setTab('calendar'); }}>
                    <span style={{ fontSize:13, color:'var(--t3)' }}>Schedule</span>
                    <span style={{ fontSize:13, color:'var(--ink)' }}>{todayEvents.length > 0 ? todayEvents[0].title : 'Nothing today'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
                <div style={{ paddingRight:'40px', borderRight:'0.5px solid var(--b1)' }}>
                  <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:12 }}>7-day momentum</div>
                  <MomentumStrip st={st} desktopMode={true} />
                </div>
                <div style={{ paddingLeft:'40px' }}>
                  <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:12 }}>at a glance</div>
                  <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', padding:'8px 0', borderBottom:'0.5px solid var(--b1)', gap:12, cursor:'pointer' }} onClick={function() { setTab('finance'); }}>
                    <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t3)', flexShrink:0, width:60 }}>finance</span>
                    <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ink)', flex:1 }}>{financeSignal ? (financeSignal.label + (financeSignal.value ? ' · ' + financeSignal.value : '')) : 'nothing due this week'}</span>
                    <span style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--accent)', flexShrink:0 }}>{(function() { var net = (st.income||[]).reduce(function(s,inc) { var a=parseFloat(inc.amount)||0; if(inc.freq==='weekly') return s+a*4.33; if(inc.freq==='bi-weekly'||inc.freq==='biweekly') return s+a*2.167; if(inc.freq==='annual') return s+a/12; return s+a; }, 0) - (st.finance||[]).filter(function(f){return f.freq==='monthly';}).reduce(function(s,f){return s+(f.amount||0);},0); return net > 0 ? '$' + Math.round(net).toLocaleString() : '—'; })()}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', padding:'8px 0', borderBottom:'0.5px solid var(--b1)', gap:12, cursor:'pointer' }} onClick={function() { setTab('health'); }}>
                    <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t3)', flexShrink:0, width:60 }}>health</span>
                    <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ink)', flex:1 }}>{healthSignal ? (healthSignal.label + (healthSignal.value !== undefined ? ' · ' + healthSignal.value : '')) : 'no workouts logged'}</span>
                    <span style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--accent)', flexShrink:0 }}>{(function() { var wks=(st.workouts||[]).filter(function(w){ var d=Math.round((new Date(TODAY)-new Date(w.date))/86400000); return d>=0&&d<7; }); return wks.length+'/7'; })()}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', padding:'8px 0', gap:12, cursor:'pointer' }} onClick={function() { setTab('calendar'); }}>
                    <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t3)', flexShrink:0, width:60 }}>schedule</span>
                    <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ink)', flex:1 }}>{todayEvents.length > 0 ? todayEvents[0].title : 'nothing today'}</span>
                    <span style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--t4)', flexShrink:0 }}>{todayEvents.length} events</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {false && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:8 }}>at a glance</div>
              <div style={{ background:'var(--s1)', border:'0.5px solid var(--b1)', borderRadius:14, overflow:'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', padding:'11px 14px', borderBottom:'0.5px solid var(--b1)', gap:12, cursor:'pointer' }} onClick={function() { setTab('finance'); }}>
                  <div style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t3)', width:64, flexShrink:0 }}>finance</div>
                  <div style={{ flex:1, minWidth:0, fontFamily:'var(--sans)', fontSize:13, color:'var(--ink)' }}>{financeSignal ? (financeSignal.label + (financeSignal.value ? ' · ' + financeSignal.value : '')) : 'nothing due this week'}</div>
                  <div style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--accent)' }}>{(function() { var net = (st.income||[]).reduce(function(s,inc) { var a=parseFloat(inc.amount)||0; if(inc.freq==='weekly') return s+a*4.33; if(inc.freq==='bi-weekly'||inc.freq==='biweekly') return s+a*2.167; if(inc.freq==='annual') return s+a/12; return s+a; }, 0) - (st.finance||[]).filter(function(f){return f.freq==='monthly';}).reduce(function(s,f){return s+(f.amount||0);},0); return net > 0 ? '$' + Math.round(net).toLocaleString() : '—'; })()}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', padding:'11px 14px', borderBottom:'0.5px solid var(--b1)', gap:12, cursor:'pointer' }} onClick={function() { setTab('health'); }}>
                  <div style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t3)', width:64, flexShrink:0 }}>health</div>
                  <div style={{ flex:1, minWidth:0, fontFamily:'var(--sans)', fontSize:13, color:'var(--ink)' }}>{healthSignal ? (healthSignal.label + (healthSignal.value !== undefined ? ' · ' + healthSignal.value : '')) : 'no workouts logged'}</div>
                  <div style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--accent)' }}>{(function() { var wks=(st.workouts||[]).filter(function(w){ var d=Math.round((new Date(TODAY)-new Date(w.date))/86400000); return d>=0&&d<7; }); return wks.length+'/7'; })()}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', padding:'11px 14px', gap:12, cursor:'pointer' }} onClick={function() { setTab('calendar'); }}>
                  <div style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t3)', width:64, flexShrink:0 }}>schedule</div>
                  <div style={{ flex:1, minWidth:0, fontFamily:'var(--sans)', fontSize:13, color:'var(--ink)' }}>{todayEvents.length > 0 ? todayEvents[0].title : 'nothing today'}</div>
                  <div style={{ fontFamily:'var(--mono)', fontSize:12, color: todayEvents.length > 0 ? 'var(--ink)' : 'var(--t4)' }}>{todayEvents.length}</div>
                </div>
              </div>
            </div>
          )}

          {/* ── GOALS ── */}
          {(st.goals||[]).filter(function(g){return !g.completed;}).length > 0 && (
            <div style={{ marginBottom:28, paddingBottom:28, borderBottom:'0.5px solid var(--b1)' }}>
              <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:12 }}>goals</div>
              {isMobile ? (
                <div style={{ display:'flex', justifyContent:'space-around', alignItems:'flex-start' }}>
                  {(st.goals||[]).filter(function(g){return !g.completed;}).slice(0,3).map(function(g) {
                    var pct = Math.round(computeGoalProgress(g));
                    var r = 26; var circ = 2 * Math.PI * r;
                    var offset = circ - (pct / 100) * circ;
                    var shortTitle = g.title.length > 12 ? g.title.slice(0,12) + '…' : g.title;
                    return (
                      <div key={g.id} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, flex:1, cursor:'pointer' }} onClick={function(){ setTab('goals'); }}>
                        <svg width="64" height="64" viewBox="0 0 64 64">
                          <circle cx="32" cy="32" r={r} fill="none" stroke="var(--b1)" strokeWidth="4"/>
                          <circle cx="32" cy="32" r={r} fill="none" stroke="var(--accent)" strokeWidth="4"
                            strokeDasharray={circ} strokeDashoffset={offset}
                            strokeLinecap="round" transform="rotate(-90 32 32)"
                            style={{opacity: pct < 20 ? 0.5 : 1}}/>
                          <text x="32" y="36" textAnchor="middle" fontSize="13" fontWeight="500" fill="var(--al)" fontFamily="var(--sans)">{pct}%</text>
                        </svg>
                        <div style={{ fontSize:10, color:'var(--t3)', textAlign:'center', lineHeight:1.3 }}>{shortTitle}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:32 }}>
                  {(st.goals||[]).filter(function(g){return !g.completed;}).slice(0,3).map(function(g) {
                    var pct = Math.round(computeGoalProgress(g));
                    return (
                      <div key={g.id} style={{ cursor:'pointer' }} onClick={function() { setTab('goals'); }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:7 }}>
                          <span style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--t2)' }}>{g.title}</span>
                          <span style={{ fontFamily:'var(--mono)', fontSize:10, color:'var(--t4)' }}>{pct}%</span>
                        </div>
                        <div style={{ height:2, background:'var(--b1)', borderRadius:1, overflow:'hidden' }}>
                          <div style={{ height:'100%', borderRadius:1, background:'var(--accent)', opacity: pct < 20 ? 0.45 : 1, width: pct + '%', transition:'width 0.3s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {false && (
                <div style={{ background:'var(--s1)', border:'0.5px solid var(--b1)', borderRadius:14, overflow:'hidden' }}>
                  {(st.goals||[]).filter(function(g){return !g.completed;}).slice(0,3).map(function(g,i,arr) {
                    var pct = Math.round(computeGoalProgress(g));
                    return (
                      <div key={g.id} style={{ padding:'10px 14px', borderBottom: i < arr.length-1 ? '0.5px solid var(--b1)' : 'none', cursor:'pointer' }} onClick={function() { setTab('goals'); }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                          <div style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--t2)' }}>{g.title}</div>
                          <div style={{ fontFamily:'var(--mono)', fontSize:10, color:'var(--t4)' }}>{pct}%</div>
                        </div>
                        <div style={{ height:2, background:'var(--b1)', borderRadius:1, overflow:'hidden' }}>
                          <div style={{ height:'100%', borderRadius:1, background:'var(--accent)', opacity: pct < 20 ? 0.45 : 1, width: pct + '%', transition:'width 0.3s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── ASK MYLO CTA ── */}
{false && <div style={{ paddingBottom:28, borderBottom:'0.5px solid var(--b1)', marginBottom:28, display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }} onClick={function() { setTab('ask'); }}>
            <div>
              <div style={{ fontFamily:'var(--mono)', fontSize:8, color:'var(--accent)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>ai assistant</div>
              <div style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ink)' }}>ask mylo anything →</div>
            </div>
            <Zap size={18} strokeWidth={1.3} style={{ color:'var(--accent)', opacity:0.6, flexShrink:0 }} />
          </div>}

          {/* ── GOALS ── */}
          {(st.goals||[]).filter(function(g){return !g.completed;}).length === 0 && (
            <div style={{ marginBottom:28, paddingBottom:28, borderBottom:'0.5px solid var(--b1)' }}>
              <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:12 }}>goals</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:32 }}>
                {[{title:'Marathon PR sub-4:00',pct:68},{title:'FIRE by 42 · $3M',pct:41},{title:'Green card · HPSA',pct:25}].map(function(g,i) { return (
                  <div key={i}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:7 }}>
                      <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--t2)' }}>{g.title}</span>
                      <span style={{ fontFamily:'var(--mono)', fontSize:10, color:'var(--t4)' }}>{g.pct}%</span>
                    </div>
                    <div style={{ height:2, background:'var(--b1)', borderRadius:1, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:g.pct+'%', background:'var(--accent)', borderRadius:1, opacity: g.pct < 30 ? 0.5 : 1 }} />
                    </div>
                  </div>
                ); })}
              </div>
            </div>
          )}

          {/* ── PAGES ── */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontFamily:'var(--sans)', fontSize:12, fontWeight:500, color:'var(--al)', marginBottom:8 }}>pages</div>
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6,1fr)', gap: isMobile ? '10px 20px' : 0 }}>
              {[{id:'health',label:'health'},{id:'finance',label:'finance'},{id:'tasks',label:'tasks'},{id:'goals',label:'goals'},{id:'calendar',label:'calendar'},{id:'vinyl',label:'vinyl'}].map(function(page) {
                var meta = (function() {
                  if (page.id === 'health') { var w=(st.workouts||[]).filter(function(x){ var d=Math.round((new Date(TODAY)-new Date(x.date))/86400000); return d>=0&&d<7; }); return w.length>0 ? w.length+' sessions' : 'no sessions'; }
                  if (page.id === 'finance') { var b=(st.finance||[]).filter(function(f){ var d=Math.round((new Date(f.next||'')-new Date(TODAY))/86400000); return d>=0&&d<=7; }); return b.length>0 ? b.length+' bill'+(b.length>1?'s':'')+' this week' : 'nothing due'; }
                  if (page.id === 'tasks') { var t=(st.tasks||[]).filter(function(x){return x.bucket==='today'||x.dueDate===TODAY;}); return t.filter(function(x){return !x.done;}).length+' open · '+t.filter(function(x){return x.done;}).length+' done'; }
                  if (page.id === 'goals') { var g=(st.goals||[]).filter(function(x){return !x.completed;}); return g.length>0 ? g.length+' active' : 'no goals'; }
                  if (page.id === 'calendar') { var e=(st.events||[]).filter(function(x){ var d=Math.round((new Date(x.date)-new Date(TODAY))/86400000); return d>=0&&d<7; }); return e.length>0 ? e.length+' this week' : 'nothing'; }
                  if (page.id === 'vinyl') { var v=(st.vinyl||[]).filter(function(x){return !x.delivered&&x.status==='shipped';}); return v.length>0 ? v.length+' in transit' : 'up to date'; }
                  return '';
                })();
                return (
                  <div key={page.id} style={{ padding: isMobile ? 0 : '0 20px 0 0', cursor:'pointer', minWidth:0 }} onClick={function() { setTab(page.id); }}>
                    <div style={{ fontFamily:'var(--sans)', fontSize: isMobile ? 13 : 13, color:'var(--t3)', marginBottom: isMobile ? 2 : 4 }}>{page.label}</div>
                    <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--t4)' }}>{meta}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {false && (
            <div style={{ display:'flex', gap:4, justifyContent:'center', padding:'12px 0 8px' }}>
              <div style={{ width:5, height:5, borderRadius:'50%', background:'var(--b2)' }} />
              <div style={{ width:18, height:3, borderRadius:2, background:'var(--accent)', opacity:0.8 }} />
            </div>
          )}
          </div>
        </div>
      </div>

      {!isMobile && <QuickAddInput {...quickAddProps} className={css.quickAddSticky} />}

    </div>
  );
}
export default DashboardPage;